import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router';
import { legacyRoutes, legacyRoutesMap, legacyPushRoute } from '@v2/legacy-bridge/routes';
import { applyLegacyVisibility } from '@v2/legacy-bridge/routes/navigationGuard';
import { useAuthStore } from '@v2/features/auth';

// Extend Router type to include legacy utilities
declare module 'vue-router' {
  interface Router {
    routesMap: Record<string, (...args: string[]) => string>;
    pushRoute: Record<string, (...args: string[]) => ReturnType<Router['push']>>;
  }
}

// v2 native routes - add new routes here
// NOTE: meta.canView is required for legacy App.vue menu filtering
// NOTE: meta.requiresAuth defaults to true for v2 routes
const v2Routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    // Redirect handled in beforeEach guard (needs Pinia to be initialized)
    component: () => import('@v2/features/auth/pages/LoginPage.vue'),
    meta: {
      canView: () => true,
      requiresAuth: false,
      isHomeRoute: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@v2/features/auth/pages/LoginPage.vue'),
    meta: {
      canView: () => true,
      requiresAuth: false,
    },
  },
  {
    path: '/profile',
    name: 'MyProfile',
    component: () => import('@v2/features/profile/pages/ProfileViewPage.vue'),
    meta: {
      canView: () => useAuthStore().isAuthenticated,
      requiresAuth: true,
    },
  },
  {
    path: '/profile/edit',
    name: 'EditProfile',
    component: () => import('@v2/features/profile/pages/ProfileEditPage.vue'),
    meta: {
      canView: () => useAuthStore().isAuthenticated,
      requiresAuth: true,
    },
  },
  {
    path: '/inbox',
    name: 'Inbox',
    component: () => import('@v2/features/inbox/pages/InboxPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/discussions',
    name: 'Discussions',
    component: () => import('@v2/features/discussions/pages/DiscussionsPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/contacts',
    redirect: '/contacts/contacts',
  },
  {
    path: '/contacts/contacts',
    name: 'Contacts',
    component: () => import('@v2/features/contacts/pages/ContactsPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/contacts/roster',
    name: 'ContactsRoster',
    component: () => import('@v2/features/contacts/pages/ContactsPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/contacts/rosters/:id',
    name: 'RosterDetail',
    component: () => import('@v2/features/contacts/pages/RosterDetailPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/discussions/create',
    name: 'CreateDiscussion',
    component: () => import('@v2/features/create-discussion/pages/CreateDiscussionPage.vue'),
    meta: {
      canView: () => !useAuthStore().publicView,
      requiresAuth: true,
    },
  },
  {
    path: '/discussions/:uuid',
    name: 'Debate',
    component: () => import('@v2/features/debate/pages/DebatePage.vue'),
    meta: {
      canView: () => true,
      requiresAuth: true,
    },
  },
  {
    path: '/join/:debateId',
    name: 'JoinDebate',
    component: () => import('@v2/features/debate/pages/JoinDebatePage.vue'),
    meta: {
      canView: () => true,
      requiresAuth: true,
    },
  },
];

// Merge v2 routes with legacy routes
// v2 routes take precedence (listed first)
const routes: RouteRecordRaw[] = [...v2Routes, ...legacyRoutes];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Attach legacy router utilities for backward compatibility
// Legacy code uses router.routesMap.RouteName(...args) to build paths
router.routesMap = legacyRoutesMap;
router.pushRoute = Object.entries(legacyRoutesMap).reduce(
  (obj, [k, v]) => ({
    ...obj,
    [k]: (...args: string[]) => router.push((v as (...args: string[]) => string)(...args)),
  }),
  {} as Record<string, (...args: string[]) => ReturnType<Router['push']>>
);

/**
 * Navigation guard for v2 and legacy routes.
 * - v2 routes with requiresAuth: ensures auth is initialized before rendering
 * - Legacy routes: applies visibility logic via legacy App.vue shell
 * - All routes: apply visibility mutations for sidebar/topbar state
 */
router.beforeEach(async (to, _from, next) => {
  const isV2Route = v2Routes.some(r => r.name === to.name || r.path === to.path);

  if (isV2Route) {
    const authStore = useAuthStore();

    // Always initialize auth for v2 routes to determine auth state
    await authStore.initialize();

    // Handle home route: redirect based on auth state
    if (to.meta?.isHomeRoute) {
      return next(authStore.isAuthenticated ? '/discussions' : '/login');
    }

    // Redirect authenticated users away from login page
    if (to.name === 'Login' && authStore.isAuthenticated) {
      return next('/discussions');
    }

    // Redirect unauthenticated users to login with returnUrl for auth-required routes
    if (to.meta?.requiresAuth && !authStore.isAuthenticated) {
      const returnUrl = encodeURIComponent(to.fullPath);
      return next(`/login?returnUrl=${returnUrl}`);
    }
  }

  // Always apply visibility logic for proper topbar/sidebar state
  applyLegacyVisibility(to);

  next();
});

export default router;
